import { useSyncExternalStore } from 'react';
import { mq } from '../ui';

/*
 * The viewport tier that drives the §9 sidebar reflow (F5-b FR-8 / AC-8), derived from `matchMedia`.
 *
 * `desktop` ≥1280 (`bp/desktop`), `laptop` 1024–1279 (`bp/laptop`), `tablet` <1024 down to the 768
 * floor (`bp/tablet`). The queries come from `mq` in `src/ui/breakpoints.ts` — the one carrier of
 * the `bp/*` values, because a custom property cannot appear inside a media-query condition, so a
 * raw pixel width must never be written here or in a CSS `@media` (repo rule / FS-3 AC-6).
 *
 * `useSyncExternalStore` rather than an effect + state: the tier is external browser state, so
 * subscribing to the three `MediaQueryList` change events re-derives it on a viewport change with
 * no tearing and no extra render on mount. The mechanism (branch selection, re-derive on change) is
 * what F5-b WI-1.1 tests in jsdom; the rendered widths and the transition are F5-b WI-2.1's.
 */
export type SidebarTier = 'desktop' | 'laptop' | 'tablet';

function hasMatchMedia(): boolean {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function currentTier(): SidebarTier {
    if (!hasMatchMedia()) {
        // No matchMedia (SSR probe / a stripped test env): the app is CSR-only (AD #21), so the
        // widest tier is the safe default — the frame renders expanded rather than as an overlay.
        return 'desktop';
    }
    if (window.matchMedia(mq.desktop).matches) {
        return 'desktop';
    }
    if (window.matchMedia(mq.laptop).matches) {
        return 'laptop';
    }
    return 'tablet';
}

function subscribe(onChange: () => void): () => void {
    if (!hasMatchMedia()) {
        return () => {};
    }
    const lists = [window.matchMedia(mq.desktop), window.matchMedia(mq.laptop), window.matchMedia(mq.tablet)];
    for (const list of lists) {
        list.addEventListener('change', onChange);
    }
    return () => {
        for (const list of lists) {
            list.removeEventListener('change', onChange);
        }
    };
}

/** The current viewport tier, re-derived whenever a `bp/*` boundary is crossed. */
export function useSidebarLayout(): SidebarTier {
    return useSyncExternalStore(subscribe, currentTier, () => 'desktop');
}
